import { QueryFactory } from '@lmt/shared-types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppointmentColorSettingsRuleDto } from '@lmt/shared-types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { Loading } from '../../common/Loading';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { ColorRulesTable } from './list/ColorRulesTable';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import styles from './ColorSettingsPage.module.scss';
import { ReactComponent as AddIcon } from '@lmt/unicorn-shared/app/icons/plus-blue.svg';

export const ColorSettingsPage: React.FC = React.memo(
  function ColorSettingsPage() {
    const globalForms = useGlobalForms();
    const dispatch = useUniDispatch();
    const i18n = useScopedTranslation('calendar.color_settings');
    const queryCache = useQueryClient();
    const colorSettingsQuery =
      QueryFactory.AppointmentColorSettingsQuery.useGetQuery();
    const visitTypes = useActiveVisitTypes();

    useEffect(() => {
      void invalidateColorRules();
    }, [visitTypes.length]);

    async function invalidateColorRules() {
      await queryCache.invalidateQueries({
        queryKey: QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
      });
    }

    useEffect(() => {
      if (colorSettingsQuery.data) {
        const positions = colorSettingsQuery.data.rules.map(
          (rule) => rule.position,
        );
        if (new Set(positions).size !== positions.length) {
          const rules = colorSettingsQuery.data.rules;
          rules.forEach((item, index) => {
            item.position = index + 1;
          });
          queryCache.setQueryData(
            QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
            { rules: rules },
          );
        }
      }
    }, [colorSettingsQuery?.data]);

    const modals = useModal();
    const onSortEnd = useCallback(
      async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const items = colorSettingsQuery.data!.rules;
        const oldIndex = items.findIndex((x) => x.position == active.id);
        const newIndex = items.findIndex((x) => x.position == over.id);

        const newData = arrayMove(
          colorSettingsQuery.data!.rules!,
          oldIndex,
          newIndex,
        );

        newData.forEach((item, index) => {
          item.position = index + 1;
        });
        queryCache.setQueryData(
          QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
          { rules: newData },
        );
        try {
          await QueryFactory.AppointmentColorSettingsClient.patch({
            rules: newData,
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      },
      [colorSettingsQuery.data],
    );

    const onEditRule = useCallback(
      (colorSettingsRule: AppointmentColorSettingsRuleDto) => {
        globalForms.openAppointmentColorSettings(colorSettingsRule);
      },
      [],
    );
    const onDeleteRule = useCallback(
      async (colorSettingsRule: AppointmentColorSettingsRuleDto) => {
        if (
          await modals.showConfirm({ text: i18n.t('delete_confirmation_text') })
        ) {
          const updatedData = colorSettingsQuery.data!.rules!.filter(
            (x) => x.position !== colorSettingsRule.position,
          )!;
          updatedData.forEach((item, index) => {
            item.position = index + 1;
          });

          queryCache.setQueryData(
            QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
            { rules: updatedData },
          );
          try {
            await QueryFactory.AppointmentColorSettingsClient.patch({
              rules: updatedData,
            });
          } catch (e) {
            await modals.showError({ text: convertToErrorString(e) });
          }
        }
      },
      [colorSettingsQuery.data, dispatch],
    );
    const onNewFilter = useCallback(() => {
      globalForms.openAppointmentColorSettings(undefined);
    }, []);

    const nonDefaultRules = useMemo(() => {
      return (
        colorSettingsQuery.data?.rules?.filter((rule) => !rule.isDefault) ?? []
      );
    }, [colorSettingsQuery.data]);
    const defaultRules = useMemo(() => {
      return (
        colorSettingsQuery.data?.rules?.filter((rule) => rule.isDefault) ?? []
      );
    }, [colorSettingsQuery.data]);

    return (
      <div className={styles.storedScrolling}>
        <Loading
          loading={colorSettingsQuery.isLoading}
          title={i18n.t('loading')}
          testId="color-settings-page"
        >
          {colorSettingsQuery.data ? (
            <div className={styles.container}>
              <div className={styles.header}>
                <div
                  className={styles.title}
                  data-test-id="custom-styles-header"
                >
                  {i18n.t('custom_settings')}
                </div>
                <Button
                  title={i18n.t('add_button')}
                  color={ButtonColor.Primary}
                  iconComponent={<AddIcon />}
                  onClick={onNewFilter}
                  testId="add-new-button"
                />
              </div>
              <ColorRulesTable
                data={nonDefaultRules}
                editRule={onEditRule}
                removeRule={onDeleteRule}
                isNoRulesFound={false}
                isDefault={false}
                onSortEnd={onSortEnd}
              />
              <div className={styles.header}>
                <div
                  className={styles.title}
                  data-test-id="default-styles-header"
                >
                  {i18n.t('default_settings')}
                </div>
              </div>
              <ColorRulesTable
                data={defaultRules}
                editRule={onEditRule}
                removeRule={onDeleteRule}
                isNoRulesFound={false}
                isDefault={true}
                onSortEnd={onSortEnd}
              />
            </div>
          ) : null}
        </Loading>
      </div>
    );
  },
);
